import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Auth callback route (PKCE).
 * - Extracts `code` from search params.
 * - Exchanges auth code for session via supabase.auth.exchangeCodeForSession(code).
 * - Handles "link already used": if exchange fails (e.g. email prefetch consumed the code, or
 *   double-mount), check for existing session — if valid, treat as success and redirect.
 * - On success: redirects to current origin (/) so it works on app.reviewboost24.com and custom domain.
 * - On error or missing code: redirects to /login?error=auth_callback_failed.
 */
const REDIRECT_DELAY_MS = 1200;
const LOGIN_ERROR_PARAM = "auth_callback_failed";

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const executed = useRef(false);

  const redirectToHome = () => {
    if (typeof window !== "undefined") {
      window.location.replace(`${window.location.origin}/`);
    } else {
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");

    if (errorParam || !code) {
      navigate(`/login?error=${LOGIN_ERROR_PARAM}`, { replace: true });
      return;
    }

    if (executed.current) return;
    executed.current = true;

    const run = async () => {
      try {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          // Link may have been consumed by email prefetch or double-mount: check if session already exists
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData.session) {
            await new Promise((r) => setTimeout(r, REDIRECT_DELAY_MS));
            redirectToHome();
            return;
          }
          executed.current = false;
          navigate(`/login?error=${LOGIN_ERROR_PARAM}`, { replace: true });
          return;
        }
        if (!data.session) {
          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData.session) {
            executed.current = false;
            navigate(`/login?error=${LOGIN_ERROR_PARAM}`, { replace: true });
            return;
          }
        }
        await new Promise((r) => setTimeout(r, REDIRECT_DELAY_MS));
        redirectToHome();
      } catch {
        // On exception, still check for existing session (handles race / double-mount)
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          await new Promise((r) => setTimeout(r, REDIRECT_DELAY_MS));
          redirectToHome();
          return;
        }
        executed.current = false;
        navigate(`/login?error=${LOGIN_ERROR_PARAM}`, { replace: true });
      }
    };

    run();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-foreground p-4">
      <div className="text-center space-y-4">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground">Confirming your account…</p>
      </div>
    </div>
  );
}
